'use client'
import { cva, VariantProps } from 'class-variance-authority'
import { ClassValue, cn, iconContainerVariants, Spinner } from 'tailwind-ui'
import { forwardRef } from 'react'
import type { NavLinkProps } from 'react-router'
import { NavLink } from 'react-router'
import { Icn, SvgIcon } from '../icon'

const navLinkVariants = cva(
  `inline-flex items-center rounded-full font-medium
    focus-visible:ring-primary-500 focus:outline-none focus-visible:ring
 
    transition-colors duration-75 disabled:cursor-not-allowed`,
  {
    variants: {
      variant: {
        brandKit: {
          true: 'rounded-button !bg-interface-500 !text-brand-invert !font-body border-brand hover:opacity-90 active:opacity-90 hover:bg-interface-400 active:bg-interface-600',
          false: 'rounded-md',
        },
        link: [
          'text-gray-500 dark:text-gray-400',

          '!px-0 !py-0',
          'hover:text-dark',
          '!min-h-[20px]',
        ],
        primary: [
          'bg-primary-500 text-white',

          'hover:bg-primary-600 hover:text-white',
          'active:bg-primary-700',
          'disabled:bg-primary-700',
        ],
        outline: [
          'text-primary-500',
          'border-primary-500 border',
          'hover:bg-primary-50 active:bg-primary-100 disabled:bg-primary-100',
        ],
        ghost: [
          'text-primary-500',
          'shadow-none',
          'hover:bg-primary-50 active:bg-primary-100 disabled:bg-primary-100',
        ],
        light: [
          'bg-white text-gray-700',

          'hover:text-dark hover:bg-gray-100',
          'active:bg-white/80 disabled:bg-gray-200',
        ],
        dark: [
          'bg-neutral-900 text-white',

          'hover:bg-gray-800 active:bg-gray-700 disabled:bg-gray-700',
        ],
        default: [
          'bg-black/10 border border-black/5 text-black/80 backdrop-blur-lg',
          'aria-[current=page]:bg-white/80 dark:aria-[current=page]:bg-white/20 aria-[current=page]:text-black dark:aria-[current=page]:text-white',
          'hover:text-white hover:bg-black/15',
          'active:bg-white/80 active:text-white disabled:bg-black/20',
          'dark:bg-white/10 dark:text-white/70 dark:hover:text-white/90 dark:border-white/5',

          'dark:hover:bg-white/15 dark:active:bg-white/20 dark:disabled:bg-white/20',
        ],
        ghost_default: [
          'text-black/70 dark:text-white/70',

          'hover:text-black hover:bg-black/10',
          'active:bg-white/80 disabled:bg-black/20',
          'dark:hover:bg-white/10 dark:active:bg-white/5 dark:disabled:bg-white/5 !min-h-[20px]',
        ],
        tab: [
          'rounded-none shadow-none whitespace-nowrap border-b-2 py-4 px-0 text-sm gap-2 font-medium flex flex-row',
        ],
      },
      size: {
        lg: ['px-6 py-2', 'text-md md:text-base'],
        base: ['px-3 py-1.5', 'text-sm'],
        sm: ['px-2 py-1', 'text-xs md:text-sm'],
      },
      isActive: {
        true: null,
        false: null,
      },
      isDarkBg: {
        true: null,
        false: null,
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'base',
      isActive: false,
      isDarkBg: false,
    },
    compoundVariants: [
      {
        //  brandKit: true,
        variant: ['ghost_default', 'link'],
        class: '!bg-transparent !text-brand',
      },
      {
        isActive: true,
        variant: ['default'],
        class: 'dark:bg-white/20 bg-black/20',
      },
      {
        isActive: true,
        variant: ['primary'],
        class: 'bg-primary-700',
      },
      {
        isActive: true,
        variant: ['ghost_default'],
        class:
          'text-neutral-900 dark:text-neutral-50  bg-neutral-100 dark:bg-neutral-800',
      },
      {
        isActive: true,
        isDarkBg: false,
        variant: ['outline'],
        class: 'bg-primary-100',
      },
      {
        isActive: true,
        isDarkBg: true,
        variant: ['outline'],
        class: 'bg-gray-800',
      },
      {
        isDarkBg: true,
        variant: ['outline', 'ghost'],
        class: 'hover:bg-neutral-900 active:bg-gray-800 disabled:bg-gray-800',
      },
      {
        isActive: true,
        variant: ['tab'],
        class:
          'border-slate-900 text-slate-900 dark:text-slate-50 dark:border-slate-50',
      },
      {
        isActive: false,
        variant: ['tab'],
        class:
          'border-transparent text-gray-500 dark:text-gray-400 hover:border-gray-300',
      },
      {
        size: 'base',
        variant: ['tab'],
        class: 'px-0 py-4',
      },
    ],
  }
)

export interface NavVariantProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof navLinkVariants> {
  isLoading?: boolean
  leftIcon?: SvgIcon
  rightIcon?: SvgIcon
  disabled?: boolean
  classNames?: {
    leftIcon?: ClassValue
    rightIcon?: ClassValue
  }
}

export const SNavLink = forwardRef<
  HTMLAnchorElement,
  NavVariantProps & NavLinkProps
>(
  (
    {
      children,
      className,
      disabled: buttonDisabled,
      isLoading,
      variant = 'default',
      size = 'base',
      isDarkBg,
      leftIcon: LeftIcon,
      rightIcon: RightIcon,
      classNames,
      ...rest
    },
    ref
  ) => {
    const disabled = isLoading || buttonDisabled
    return (
      <NavLink
        className={({ isActive, isPending }) =>
          cn(
            'touch-manipulation',
            navLinkVariants({ variant, size, isDarkBg, isActive, className }),
            {
              ['relative  cursor-wait disabled:cursor-wait']:
                isLoading || isPending,
              /*
              ['[&>.content]:bg-gray-100 [&>.content]:text-gray-800']:
                isActive && variant === 'tab',
              ['[&>.content]:px-2']: variant === 'tab',
              */
            }
          )
        }
        ref={ref}
        {...rest}
      >
        {({ isPending }) => (
          <>
            {LeftIcon ? (
              <div
                className={cn(
                  iconContainerVariants({ size, isLeft: !!children })
                )}
              >
                <Icn
                  icon={LeftIcon}
                  size={size}
                  className={cn(
                    size === 'lg' && 'h-6 w-6',
                    classNames?.leftIcon
                  )}
                />
              </div>
            ) : null}
            {children && (
              <div className={cn('content rounded-md')}>{children}</div>
            )}

            {RightIcon && !(isLoading || isPending) ? (
              <div
                className={cn(iconContainerVariants({ size, isRight: true }))}
              >
                <Icn
                  icon={RightIcon}
                  size={size}
                  className={cn(
                    size === 'lg' && 'h-6 w-6',
                    classNames?.rightIcon
                  )}
                />
              </div>
            ) : null}
            {children && (isLoading || isPending) ? (
              <Spinner className={cn('ml-auto')} />
            ) : null}
          </>
        )}
      </NavLink>
    )
  }
)

SNavLink.displayName = 'SNavLink'
